import type {CSSObject} from '@mantine/core';

import {
  REDESIGN_HEADER_ICON_CHIP_BACKGROUND,
  REDESIGN_HEADER_ICON_CHIP_PADDING_PX,
  REDESIGN_HEADER_ICON_CHIP_RADIUS_PX,
  REDESIGN_HEADER_ICON_CHIP_SIZE_PX,
  REDESIGN_HEADER_ICON_COLOR,
  REDESIGN_HEADER_ICON_GLYPH_SIZE_PX,
} from '../../ui/AIStyleConstants';

/**
 * The redesigned header's icon-button chip: a borderless glyph in a square with a rounded hover
 * background. Shared by the sidebar toggle (hamburger), new chat, chat settings, and the panel
 * expand/collapse controls.
 *
 * The box is a fixed square and the glyph is constrained to a square (rather than being sized by font
 * size, which leaves the width to the glyph) so chips don't change shape with the glyph's aspect
 * ratio — a double chevron is wider than a gear, and left to itself renders a rectangle beside
 * everyone else's square. This mirrors how `ui-library` sizes the small icon button that the header's
 * share button uses, so that button lands on the same geometry.
 *
 * Spread this into a `createStyles` rule. The doubled `&&` beats `ActionIcon`'s own sizing, which
 * otherwise forces a different box and svg size.
 */
export const redesignHeaderIconChipStyles: CSSObject = {
  '&&': {
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: REDESIGN_HEADER_ICON_CHIP_SIZE_PX,
    height: REDESIGN_HEADER_ICON_CHIP_SIZE_PX,
    minWidth: 0,
    minHeight: 0,
    // ActionIcon's transparent border would offset the glyph relative to the share button's box.
    border: 0,
    padding: REDESIGN_HEADER_ICON_CHIP_PADDING_PX,
    borderRadius: REDESIGN_HEADER_ICON_CHIP_RADIUS_PX,
    color: REDESIGN_HEADER_ICON_COLOR,
  },
  '&&:hover': {
    backgroundColor: REDESIGN_HEADER_ICON_CHIP_BACKGROUND,
  },
  // A square viewport for the glyph; FontAwesome's svgs keep their aspect ratio and center inside it.
  '&& svg': {
    width: REDESIGN_HEADER_ICON_GLYPH_SIZE_PX,
    height: REDESIGN_HEADER_ICON_GLYPH_SIZE_PX,
  },
};
